import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  FormControlLabel,
  Switch,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import { TaskEditor } from './TaskEditor';
import {
  useCreateWorkPathTemplate,
  useUpdateWorkPathTemplate,
  type WorkPathTemplate,
  type WorkPathTask,
} from '@/hooks/useWorkPaths';
import { useServices, type Service } from '@/hooks/useServices';
import { useNotify } from '@/stores/notificationStore';

import { buildTemplatePayload } from './templatePayload';

interface Props {
  open: boolean;
  onClose: () => void;
  template: WorkPathTemplate | null;
}


export function WorkPathTemplateFormDialog({ open, onClose, template }: Props) {
  const { t } = useTranslation('workpaths');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const isEditing = !!template;

  const createTemplate = useCreateWorkPathTemplate();
  const updateTemplate = useUpdateWorkPathTemplate();
  const { data: services } = useServices({ isActive: true });

  const [tab, setTab] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [tasks, setTasks] = useState<WorkPathTask[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDescription(template.description ?? '');
      setIsDefault(template.isDefault);
      setSelectedServiceIds(template.serviceIds ?? []);
      setTasks((template.tasks as WorkPathTask[]) ?? []);
    } else {
      setName('');
      setDescription('');
      setIsDefault(false);
      setSelectedServiceIds([]);
      setTasks([]);
    }
    setTab(0);
    setError(null);
  }, [template, open]);

  const handleSave = async () => {
    setError(null);

    if (!name.trim()) {
      setError(t('errors.nameRequired'));
      setTab(0);
      return;
    }
    if (tasks.length === 0) {
      setError(t('errors.noTasks'));
      setTab(1);
      return;
    }
    if (tasks.some((task) => !task.name.trim())) {
      setError(t('errors.taskNameRequired'));
      setTab(1);
      return;
    }
    if (
      tasks.some((task) =>
        task.verifications.some((v) => !v.label.trim()),
      )
    ) {
      setError(t('errors.verificationLabelRequired'));
      setTab(1);
      return;
    }

    try {
      const payload = buildTemplatePayload({
        name,
        description,
        isDefault,
        selectedServiceIds,
        tasks,
        sortOrder: template?.sortOrder ?? 0,
      });


      if (isEditing) {
        await updateTemplate.mutateAsync({ id: template.id, ...payload });
        notify.success(t('template.updated'));
      } else {
        await createTemplate.mutateAsync(payload);
        notify.success(t('template.created'));
      }
      onClose();
    } catch (err) {
      // Show what the server said, in the dialog's own Alert. A bare "Virhe"
      // toast with the dialog left open is why the uuid rejection above went
      // unnoticed for so long — the message was there, nothing read it.
      const message = err instanceof Error && err.message !== '' ? err.message : tc('error');
      setError(message);
      notify.error(message);
    }
  };

  const selectedServices = (services ?? []).filter((s: Service) =>
    selectedServiceIds.includes(s.id),
  );
  const isPending = createTemplate.isPending || updateTemplate.isPending;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEditing ? t('template.edit') : t('template.create')}
      onSubmit={() => void handleSave()}
      isDirty={name !== '' || tasks.length > 0}
      isLoading={isPending}
      maxWidth="md"
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, px: 3 }}>
        <Tabs value={tab} onChange={(_e, v: number) => setTab(v)}>
          <Tab label={t('template.tabGeneral')} />
          <Tab label={t('template.tabTasks')} />
        </Tabs>
      </Box>

      <Box sx={{ px: 3, pb: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {tab === 0 && (
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label={t('template.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
              size="small"
            />
            <TextField
              label={t('template.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                />
              }
              label={t('template.isDefault')}
            />
            {isDefault && (
              <Alert severity="info" variant="outlined" sx={{ mt: -1, py: 0 }}>
                {t('template.isDefaultHint')}
              </Alert>
            )}
            <Autocomplete
              multiple
              size="small"
              options={services ?? []}
              getOptionLabel={(option: Service) => option.name}
              value={selectedServices}
              onChange={(_e, newValue) =>
                setSelectedServiceIds(newValue.map((s: Service) => s.id))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('template.serviceLinks')}
                  helperText={t('template.serviceLinksHint')}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option: Service, index) => (
                  <Chip
                    label={option.name}
                    size="small"
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
            />
          </Box>
        )}

        {tab === 1 && <TaskEditor tasks={tasks} onChange={setTasks} />}
      </Box>
    </FormDialog>
  );
}
